import { Component, forwardRef, inject, Input, OnDestroy } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { addDays, format, parseISO, subDays } from 'date-fns';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';
import { dateRangeValidator } from '../validators/validators';

export interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

@Component({
  selector: 'app-date-range-input',
  imports: [ReactiveFormsModule, InputWrapperComponent],
  templateUrl: './date-range-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateRangeInputComponent),
      multi: true,
    },
  ],
})
export class DateRangeInputComponent
  implements ControlValueAccessor, Validator, OnDestroy
{
  private _fb = inject(FormBuilder);

  @Input() label?: string;
  @Input() max: string = format(new Date(), 'yyyy-MM-dd');

  readonly form: FormGroup;

  private onChange: (value: DateRange) => void = () => {};
  private onTouched: () => void = () => {};
  private subscription: Subscription;

  constructor() {
    this.form = this._fb.group(
      {
        startDate: [null],
        endDate: [null],
      },
      { validators: dateRangeValidator },
    );

    this.subscription = this.form.valueChanges.subscribe((value) => {
      this.onChange(value as DateRange);
      this.onTouched();
    });
  }

  writeValue(value: DateRange | null): void {
    this.form.patchValue(
      {
        startDate: value?.startDate ?? null,
        endDate: value?.endDate ?? null,
      },
      { emitEvent: false },
    );
  }

  registerOnChange(fn: (value: DateRange) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  validate(): ValidationErrors | null {
    return this.form.errors;
  }

  get maxStartDate(): string {
    const endDate = this.form.value.endDate;
    return endDate ? format(subDays(parseISO(endDate), 1), 'yyyy-MM-dd') : this.max;
  }

  get minEndDate(): string | undefined {
    const startDate = this.form.value.startDate;
    return startDate
      ? format(addDays(parseISO(startDate), 1), 'yyyy-MM-dd')
      : undefined;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
